import { useCallback, useEffect, useRef, useState } from "react";
/**
 * Hook for managing auto-scroll behavior in chat/streaming content scenarios
 *
 * Features:
 * - Uses callback ref to properly handle element mount/unmount/remount
 * - Auto scroll to bottom for new content (streaming-friendly)
 * - Smart disable when user scrolls up manually
 * - Show/hide scroll-to-bottom button based on position
 * - Re-enable auto scroll when manually scrolling to bottom
 * - Optional automatic scroll to bottom on mount (with retry for late-loading assets)
 * - Selector mode: find scroll container via CSS selector (for parent layout scenarios)
 *
 * @param options Configuration options
 * @returns Scroll management utilities and state
 */
export const useScrollToBottom = ({ threshold = 10, hasContent = true, autoScroll = true, scrollToBottomOnMount = false, selector, } = {}) => {
    // Use state to track the actual DOM element (enables proper effect re-runs on mount/remount)
    const [scrollContainer, setScrollContainer] = useState(null);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(autoScroll);
    const [isNearBottom, setIsNearBottom] = useState(true);
    const isUserScrollingRef = useRef(false);
    // Track the previous hasContent to detect transitions
    const prevHasContentRef = useRef(hasContent);
    // Callback ref - called when element mounts/unmounts/changes
    // In selector mode, this is a no-op
    const scrollContainerRef = useCallback((node) => {
        if (selector)
            return; // No-op in selector mode
        setScrollContainer(node);
    }, [selector]);
    // Selector mode: find scroll container via CSS selector
    useEffect(() => {
        if (!selector)
            return;
        const element = document.querySelector(selector);
        setScrollContainer(element);
        // No cleanup needed - the element is managed by the parent layout
    }, [selector]);
    // Check scroll position and update states
    const checkScrollPosition = useCallback(() => {
        if (!scrollContainer)
            return;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
        const isClose = scrollTop + clientHeight >= scrollHeight - 150; // For auto scroll
        // If user manually scrolls up from bottom, disable auto scroll
        // Only disable if we were previously near bottom and user is not programmatically scrolling
        if (isNearBottom && !isClose && !isUserScrollingRef.current) {
            setIsAutoScrollEnabled(false);
        }
        // Re-enable auto scroll if user scrolls back to near bottom manually
        if (!isNearBottom && isClose && !isUserScrollingRef.current) {
            setIsAutoScrollEnabled(true);
        }
        setIsNearBottom(isClose);
        setShowScrollToBottom(!isAtBottom && hasContent);
    }, [scrollContainer, threshold, hasContent, isNearBottom]);
    // Scroll to bottom of container (for manual user action)
    const scrollToBottom = useCallback(() => {
        if (!scrollContainer)
            return;
        isUserScrollingRef.current = true;
        scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: "smooth", // Keep smooth for manual user action
        });
        // Re-enable auto scroll when manually scrolling to bottom
        setIsAutoScrollEnabled(true);
        // Reset user scrolling flag after scroll completes
        setTimeout(() => {
            isUserScrollingRef.current = false;
        }, 300);
    }, [scrollContainer]);
    // Auto scroll to bottom for streaming content
    const triggerAutoScroll = useCallback(() => {
        if (!isAutoScrollEnabled)
            return;
        if (!scrollContainer)
            return;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
        // Only auto scroll if we're not already at the bottom
        if (isAtBottom)
            return;
        // Mark as programmatic scroll to avoid triggering disable logic
        isUserScrollingRef.current = true;
        // Use instant scroll for streaming content to keep up with new content
        scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: "instant",
        });
        // Reset flag quickly for streaming
        setTimeout(() => {
            isUserScrollingRef.current = false;
            // Check position after scroll completes
            checkScrollPosition();
        }, 50);
    }, [scrollContainer, isAutoScrollEnabled, threshold, checkScrollPosition]);
    // Add scroll event listener - re-binds when scrollContainer changes
    useEffect(() => {
        if (!scrollContainer)
            return;
        const handleScroll = checkScrollPosition;
        scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            scrollContainer.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainer, checkScrollPosition]);
    // Check initial scroll position when container changes
    useEffect(() => {
        if (!scrollContainer)
            return;
        checkScrollPosition();
    }, [scrollContainer, checkScrollPosition]);
    // Handle scroll to bottom on mount/remount
    // Uses double requestAnimationFrame + 250ms delayed retry for late-loading assets (images/fonts)
    useEffect(() => {
        if (!scrollToBottomOnMount)
            return;
        if (!scrollContainer)
            return;
        if (!hasContent)
            return;
        // Track if this effect instance is still active (not cleaned up)
        let isActive = true;
        requestAnimationFrame(() => {
            if (!isActive || !scrollContainer)
                return;
            // Force to bottom (avoid smooth; this is not a user-initiated action)
            scrollContainer.scrollTo({
                top: scrollContainer.scrollHeight,
                behavior: "auto",
            });
            // Layout can still settle on the next frame (e.g. message rendering)
            requestAnimationFrame(() => {
                if (!isActive || !scrollContainer)
                    return;
                scrollContainer.scrollTo({
                    top: scrollContainer.scrollHeight,
                    behavior: "auto",
                });
            });
            // Content like images/fonts can load a bit later and increase scrollHeight.
            setTimeout(() => {
                if (!isActive || !scrollContainer)
                    return;
                const atBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >=
                    scrollContainer.scrollHeight - threshold;
                if (!atBottom) {
                    scrollContainer.scrollTo({
                        top: scrollContainer.scrollHeight,
                        behavior: "auto",
                    });
                }
            }, 250);
        });
        return () => {
            isActive = false;
        };
    }, [scrollContainer, hasContent, scrollToBottomOnMount, threshold]);
    // Track hasContent changes
    useEffect(() => {
        prevHasContentRef.current = hasContent;
    }, [hasContent]);
    return {
        scrollContainerRef,
        showScrollToBottom,
        scrollToBottom,
        checkScrollPosition,
        isAutoScrollEnabled,
        setAutoScrollEnabled: setIsAutoScrollEnabled,
        triggerAutoScroll,
    };
};
//# sourceMappingURL=useScrollToBottom.js.map